// RomDrops Early Access Page - Improved Version
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Paper,
  Divider,
  Chip,
  Avatar,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// RomDrops Color Palette
const COLORS = {
  blushRose: "#FF6BAF",
  romanceRed: "#FF4E50",
  softCream: "#FFF9F5",
  duskGray: "#444",
  silverMist: "#B8B8B8",
};

const GradientTypography = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(90deg, ${COLORS.romanceRed} 0%, ${COLORS.blushRose} 100%)`,
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  display: "inline",
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(90deg, ${COLORS.romanceRed} 0%, ${COLORS.blushRose} 100%)`,
  color: "white",
  borderRadius: "50px",
  padding: theme.spacing(1.5, 3),
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    background: `linear-gradient(90deg, ${COLORS.romanceRed} 0%, ${COLORS.blushRose} 100%)`,
    opacity: 0.9,
    boxShadow: theme.shadows[4],
  },
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 107, 175, 0.05)",
    transform: "translateX(5px)",
  },
}));

const PulseDot = styled(Box)({
  height: 8,
  width: 8,
  borderRadius: "50%",
  backgroundColor: COLORS.blushRose,
  display: "inline-block",
  marginRight: 8,
  animation: "pulse 2s infinite",
  "@keyframes pulse": {
    "0%": {
      transform: "scale(0.95)",
      boxShadow: "0 0 0 0 rgba(255, 107, 175, 0.7)",
    },
    "70%": {
      transform: "scale(1)",
      boxShadow: "0 0 0 10px rgba(255, 107, 175, 0)",
    },
    "100%": {
      transform: "scale(0.95)",
      boxShadow: "0 0 0 0 rgba(255, 107, 175, 0)",
    },
  },
});

const EarlyAccessPage = () => {
  const [signupCount, setSignupCount] = useState(2290);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    let count = 2290;
    const target = 2317;
    const interval = setInterval(() => {
      count += 1;
      setSignupCount(count);
      if (count >= target) clearInterval(interval);
    }, 60);
    return () => clearInterval(interval);
  }, []);

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    try {
      // Show loading state immediately
      setStatus("loading");

      const res = await fetch(
        "https://romdrops-presignups-worker.romdropsapp.workers.dev/api/presignup",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Error submitting email:", error);
      setStatus("error");
    }
  };

  const testimonials = [
    {
      text: "My boyfriend is finally surprising me with things I actually like!",
      author: "Ashley, 26",
    },
    {
      text: "It helped me remember our anniversary for once. She was so happy!",
      author: "Mike, 29",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #FFF9F5 0%, #FFE8F0 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 2,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={5}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Left Side - Form */}
          <Box
            sx={{
              flex: "1 1 50%",
              p: { xs: 3, sm: 5 },
              bgcolor: "white",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight="bold"
              mb={1}
            >
              Never Miss a Romantic Moment with{" "}
              <GradientTypography variant="inherit">
                RomDrops
              </GradientTypography>
            </Typography>

            <Typography variant="subtitle1" color="text.secondary" mb={4}>
              Perfect timing. Perfect gestures. Happy partner.
            </Typography>

            <Box sx={{ mb: 4 }}>
              <FeatureItem>
                <CheckCircleIcon sx={{ color: COLORS.blushRose, mr: 2 }} />
                <Typography>
                  Get alerts when it's the perfect time for romance
                </Typography>
              </FeatureItem>

              <FeatureItem>
                <CheckCircleIcon sx={{ color: COLORS.blushRose, mr: 2 }} />
                <Typography>
                  Simple ideas that match what she actually wants
                </Typography>
              </FeatureItem>

              <FeatureItem>
                <CheckCircleIcon sx={{ color: COLORS.blushRose, mr: 2 }} />
                <Typography>
                  Date night generator with budget-friendly options
                </Typography>
              </FeatureItem>

              <FeatureItem>
                <CheckCircleIcon sx={{ color: COLORS.blushRose, mr: 2 }} />
                <Typography>
                  Never forget an important date or moment again
                </Typography>
              </FeatureItem>
            </Box>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="outlined"
                placeholder="Enter your email for early access"
                required
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    "&:hover fieldset": {
                      borderColor: COLORS.blushRose,
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <FavoriteBorderIcon sx={{ color: COLORS.blushRose }} />
                    </InputAdornment>
                  ),
                }}
              />

              <GradientButton
                type="submit"
                fullWidth
                size="large"
                disabled={status === "loading"}
                sx={{ mb: 2 }}
              >
                {status === "loading" ? "Joining..." : "Join the Waitlist →"}
              </GradientButton>

              {status === "success" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "success.main",
                    mb: 2,
                  }}
                >
                  <CheckCircleIcon sx={{ mr: 1 }} />
                  <Typography>
                    You're on the list! We'll be in touch soon.
                  </Typography>
                </Box>
              )}

              {status === "error" && (
                <Typography color="error" sx={{ mb: 2, textAlign: "center" }}>
                  Oops! Something went wrong. Please try again.
                </Typography>
              )}
            </form>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PulseDot />
              <Typography variant="body2" color="text.secondary">
                <strong>{signupCount.toLocaleString()}</strong> couples joined
                in the last 48 hours
              </Typography>
            </Box>

            <Chip
              icon={<AccessTimeIcon />}
              label="Limited spots available for early access"
              sx={{
                alignSelf: "center",
                mt: 2,
                bgcolor: "rgba(255, 107, 175, 0.1)",
                color: COLORS.duskGray,
                fontWeight: "medium",
              }}
            />
          </Box>

          {/* Right Side - Image & Testimonials */}
          <Box
            sx={{
              flex: "1 1 50%",
              bgcolor: COLORS.softCream,
              p: { xs: 3, sm: 5 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Phone Image */}
            <Box
              sx={{
                width: "100%",
                height: "auto",
                mb: 4,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img
                src="screen1.png"
                alt="RomDrops App Preview"
                style={{
                  maxWidth: "80%",
                  height: "auto",
                  borderRadius: 16,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                }}
              />
            </Box>

            <Divider sx={{ mb: 4 }}>
              <Chip label="What Couples Say" />
            </Divider>

            {/* Testimonials */}
            <Box>
              {testimonials.map((testimonial, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: "white",
                    borderRadius: 2,
                    boxShadow: "0 3px 15px rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontStyle: "italic" }}
                  >
                    "{testimonial.text}"
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 1,
                        bgcolor: COLORS.blushRose,
                      }}
                    >
                      {testimonial.author[0]}
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">
                      {testimonial.author}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default EarlyAccessPage;
